import { ColorEnum } from '@/constants/color'
import AccountBadge from '@/features/openTabs/components/AccountBadge'
import type { AccountListStatus } from '@/features/openTabs/components/AccountList/parts/StatusTabs'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import { createStyleSheet } from 'react-native-unistyles'

export interface AccountListItem {
    id: string
    number: number
    name: string
    total: string
    paymentCount?: number
}

interface AccountRowProps {
    account: AccountListItem
    status: AccountListStatus
    selected: boolean
    onPress: () => void
}

const AccountRow: FC<AccountRowProps> = ({ account, status, selected, onPress }) => {
    const { t } = useTranslation('app')

    return (
        <Pressable
            accessibilityRole="button"
            aria-selected={selected}
            onPress={onPress}
            style={({ pressed }) => [
                styles.container,
                selected && styles.selectedContainer,
                pressed && styles.pressedContainer,
            ]}
        >
            <AccountBadge number={account.number} />
            <View style={styles.nameContainer}>
                <Typography.Description font="medium" size="large" numberOfLines={1}>
                    {account.name}
                </Typography.Description>
                {status === 'closed' && account.paymentCount !== undefined && (
                    <Typography.Description size="small" style={styles.secondaryText}>
                        {t('openTabs.accountList.paymentCount', {
                            count: account.paymentCount,
                        })}
                    </Typography.Description>
                )}
            </View>
            <View style={styles.priceContainer}>
                <Typography.Description
                    font="bold"
                    size="large"
                    numberOfLines={1}
                    style={styles.price}
                >
                    {account.total}
                </Typography.Description>
                <Typography.Description size="small" font="medium" style={styles.price}>
                    {t(`openTabs.accountList.state.${status}`)}
                </Typography.Description>
            </View>
        </Pressable>
    )
}

const styles = createStyleSheet({
    container: {
        height: 60,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        paddingHorizontal: 16,
        backgroundColor: ColorEnum.WHITE,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_200,
    },
    pressedContainer: {
        opacity: 0.72,
    },
    selectedContainer: {
        backgroundColor: '#FFF7E1',
    },
    nameContainer: {
        flex: 1,
        minWidth: 0,
        gap: 2,
    },
    priceContainer: {
        alignItems: 'flex-end',
        gap: 2,
    },
    secondaryText: {
        color: ColorEnum.GRAY_600,
    },
    price: {
        color: ColorEnum.EMERALD_700,
    },
})

export default AccountRow
